import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Banner, Icon, Pill, Segmented, StatusLabel } from '@/components/ui';
import { DISCLAIMERS } from '@/lib/copy';
import { useMe } from '@/api/hooks/core';
import { TIMEFRAMES, allowedTimeframes, runStatusTone, weightName, weightShare, type WeightPreset } from '@/lib/domain';
import { cx } from '@/lib/cx';
import { upgradePrompt } from '@/lib/upgradePrompt';
import s from './Domain.module.css';

/**
 * The simulation line. Every view that shows a result carries it, verbatim, visible without
 * scrolling past the result (web_app_design.md §2; marketing/compliance.md §4.1.3).
 */
export function SimulationLine({ aside, compact }: { aside?: ReactNode; compact?: boolean }) {
  if (compact) return <span className={s.simCompact}>{DISCLAIMERS.backtestResult}</span>;
  return <Banner aside={aside}>{DISCLAIMERS.backtestResult}</Banner>;
}

/** A strategy, as a chip that links to it. */
export function StrategyChip({ id, name, muted }: { id: string; name: string; muted?: boolean }) {
  return (
    <Link
      to="/strategies/$strategyId"
      params={{ strategyId: id }}
      className={cx(s.chip, muted && s.chipMuted)}
      title="Open strategy"
      onClick={(e) => e.stopPropagation()}
    >
      <Icon name="tune" size={15} />
      <span className={s.ellipsis}>{name}</span>
    </Link>
  );
}

export function InstrumentLabel({
  symbol,
  name,
  closed,
  size = 'md',
}: {
  symbol: string;
  name?: string | null;
  closed?: boolean;
  size?: 'md' | 'lg';
}) {
  return (
    <span className={s.instrument}>
      <span className={s.symbolRow}>
        <span className={size === 'lg' ? s.symbolLg : s.symbol}>{symbol}</span>
        {closed && <Pill size="xs">Closed</Pill>}
      </span>
      {name && <span className={s.instrumentName}>{name}</span>}
    </span>
  );
}

export interface ConditionRowData {
  rule_id: string;
  description?: string | null;
  indicator_type: string;
  operator: string;
  timeframe?: string | null;
  weight: string;
  is_required: boolean;
  satisfied?: boolean | null;
}

/**
 * A strategy's conditions with their state, weight and share of the score. `satisfied` absent
 * means the list is a definition (no state); null means "not evaluated here yet", never "not met".
 */
export function ConditionList({
  conditions,
  presets,
  showState = true,
  dense,
  chart,
}: {
  conditions: ConditionRowData[];
  presets?: WeightPreset[];
  showState?: boolean;
  dense?: boolean;
  /**
   * The chart these conditions are drawn on, when there is one beside the list. A condition on
   * another timeframe is not drawn there (web_app_design.md §4), and its row says so with the one
   * click that moves the chart — the phone's card note, kept the same.
   */
  chart?: { timeframe: string; allowed: Set<string>; onShow: (timeframe: string) => void };
}) {
  const weights = conditions.map((c) => c.weight);
  return (
    <ul className={cx(s.conditions, dense && s.dense)}>
      {conditions.map((c) => {
        const state = c.satisfied;
        const icon = state === true ? 'check_circle' : state === false ? 'cancel' : 'radio_button_unchecked';
        const tone = state === true ? s.met : state === false ? s.unmet : s.unknown;
        const off = chart && c.timeframe && c.timeframe !== chart.timeframe ? c.timeframe : null;
        return (
          <li key={c.rule_id} className={s.condition}>
            {showState && <Icon name={icon} size={18} className={tone} label={state === true ? 'Met' : state === false ? 'Not met' : 'Not evaluated yet'} />}
            <span className={s.conditionText}>
              {c.description ?? `${c.indicator_type} ${c.operator.replace(/_/g, ' ')}`}
              {c.timeframe && !c.description?.includes(` on ${c.timeframe}`) && <span className={s.tf}> · {c.timeframe}</span>}
              {off && (
                <span className={s.offNote}>
                  Not on the {chart!.timeframe} chart
                  {chart!.allowed.has(off) && (
                    <button type="button" className={s.offShow} onClick={() => chart!.onShow(off)}>
                      Show on {off}
                    </button>
                  )}
                </span>
              )}
            </span>
            {!c.is_required && <Pill tone="outline" size="xs">Optional</Pill>}
            <Pill tone="brand" size="xs">{weightName(c.weight, presets)}</Pill>
            <span className={s.share}>≈ {Math.round(weightShare(c.weight, weights))}% of score</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The chart timeframe picker. A timeframe outside the plan shows a lock and opens the upgrade
 * prompt; one the plan has but the instrument does not (a stock's 12h) is only greyed — no upgrade
 * gives a stock a 12h bar, so it carries no padlock.
 */
export function TimeframePicker({
  value,
  onChange,
  allowed,
  only,
  counts,
}: {
  value: string;
  onChange: (tf: string) => void;
  allowed: Set<string>;
  only?: readonly string[];
  /**
   * How many conditions are evaluated on each timeframe, shown as a count on the chip. A chart
   * draws a condition only on its own timeframe (§4), so this is where the others are found —
   * read off the picker before clicking, not one row at a time.
   */
  counts?: Record<string, number>;
}) {
  const list = only ?? TIMEFRAMES;
  // Before the profile arrives every gap reads as a plan lock, as it did before capabilities.
  const ent = useMe().data?.entitlements;
  const plan = ent ? allowedTimeframes(ent) : allowed;
  return (
    <Segmented
      variant="chips"
      mono
      ariaLabel="Timeframe"
      value={value}
      onChange={onChange}
      onLocked={(tf) => upgradePrompt.getState().show({ plan: 'pro', subject: `The ${tf} timeframe` })}
      options={list.map((tf) => ({
        value: tf,
        label: tf,
        locked: !plan.has(tf),
        disabled: plan.has(tf) && !allowed.has(tf),
        badge: counts?.[tf],
        title: allowed.has(tf) ? undefined : plan.has(tf) ? 'This instrument has no bars at this timeframe.' : 'This timeframe is not in your plan.',
      }))}
    />
  );
}

export function RunStatus({ status, progress }: { status: string; progress?: ReactNode }) {
  const st = runStatusTone(status);
  return (
    <span className={s.runStatus}>
      <StatusLabel tone={st.tone} live={st.live}>
        {st.label}
      </StatusLabel>
      {progress}
    </span>
  );
}
